import { config, getBackendSrv } from '@grafana/runtime';
import { lastValueFrom } from 'rxjs';
import { useState, useEffect, useCallback } from 'react';
import { ASSISTANT_PLUGIN_ID } from '../plugin';

/**
 * Event name dispatched when limits are updated via SSE.
 * Other components can listen to this event to get real-time updates.
 */
export const LIMITS_UPDATED_EVENT = 'grafana-assistant-limits-updated';

/**
 * Detail payload for the LIMITS_UPDATED_EVENT custom event.
 */
export interface LimitsUpdatedEventDetail {
  count: number;
  limit: number;
}

/**
 * Result type for limits data.
 */
export interface LimitsData {
  /** Current prompt count for the month */
  count: number;
  /** Monthly prompt limit (0 = unlimited) */
  limit: number;
  /** Current month in YYYY-MM format */
  month: string;
  /** Whether the limit has been reached */
  isLimitReached: boolean;
}

/**
 * Internal function that fetches limits data from the API.
 */
async function fetchLimitsData(): Promise<LimitsData> {
  const backendSrv = getBackendSrv();
  const appSubUrl = config?.appSubUrl ?? '';
  const apiUrl = `${appSubUrl}/api/plugins/${ASSISTANT_PLUGIN_ID}/resources/api/v1/usage/limits/prompt`;

  const response = await lastValueFrom(
    backendSrv.fetch({
      url: apiUrl,
      method: 'GET',
      showErrorAlert: false,
    })
  );

  // The API returns { status: "success", data: { total, limit, month } }
  const responseBody = response.data as { data: { total: number; limit: number; month: string } };
  const limitsData = responseBody.data;

  const count = limitsData.total ?? 0;
  const limit = limitsData.limit ?? 0;
  const month = limitsData.month ?? '';

  return {
    count,
    limit,
    month,
    isLimitReached: limit > 0 && count >= limit,
  };
}

/**
 * Checks whether the user has reached their monthly prompt limit.
 *
 * @returns A promise that resolves to `true` if limits are reached, `false` otherwise
 * @throws Error if the API call fails
 *
 * @example
 * ```typescript
 * import { checkLimits } from '@grafana/assistant';
 *
 * const data = await checkLimits();
 * if (data.isLimitReached) {
 *   console.log('Monthly limit reached');
 * }
 * ```
 */
export async function checkLimits(): Promise<LimitsData> {
  try {
    return await fetchLimitsData();
  } catch (error) {
    throw new Error(`Failed to check limits: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * React hook to check user prompt limits.
 * Automatically fetches limits data on mount and listens to real-time updates
 * via the LIMITS_UPDATED_EVENT custom event.
 *
 * @returns An object containing limit data, loading state, error state, and refetch function
 *
 * @example
 * ```typescript
 * import { useLimits } from '@grafana/assistant';
 *
 * function MyComponent() {
 *   const { count, limit, isLimitReached, loading, error, refetch } = useLimits();
 *
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   if (isLimitReached) return <div>Monthly limit reached ({count}/{limit})</div>;
 *   return <div>Prompts used: {count}/{limit || 'unlimited'}</div>;
 * }
 * ```
 */
export function useLimits() {
  const [count, setCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(0);
  const [month, setMonth] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLimits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchLimitsData();
      setCount(result.count);
      setLimit(result.limit);
      setMonth(result.month);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch limits';
      setError(errorMessage);
      setCount(0);
      setLimit(0);
      setMonth('');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  // Listen for real-time updates via custom event
  useEffect(() => {
    const handleLimitsUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<LimitsUpdatedEventDetail>;
      if (customEvent.detail) {
        setCount(customEvent.detail.count);
        setLimit(customEvent.detail.limit);
        // Clear any previous error since we received valid data via SSE
        setError(null);
      }
    };

    window.addEventListener(LIMITS_UPDATED_EVENT, handleLimitsUpdate);

    return () => {
      window.removeEventListener(LIMITS_UPDATED_EVENT, handleLimitsUpdate);
    };
  }, []);

  // Calculate isLimitReached based on current state
  const isLimitReached = limit > 0 && count >= limit;

  return {
    count,
    limit,
    month,
    isLimitReached,
    loading,
    error,
    refetch: fetchLimits,
  };
}
